import React, { useState, useCallback } from 'react';
import { Button, Space, Row, Col, AutoComplete } from '../';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { useLazyGetTagsQuery } from '../../api';
import { RivermeadowTag } from '../RivermeadowTag';
import './TagsSelector.scss';

const TagsSelector = ({
    value = [],
    onChange,
    placeholder = 'Enter tag name',
    className = '',
    style = {},
    disabled = false,
}) => {
    const [newTag, setNewTag] = useState('');
    const [suggestedTags, setSuggestedTags] = useState([]);
    const [existingTags, setExistingTags] = useState(new Set()); // Track which tags exist in system
    const [getTags] = useLazyGetTagsQuery();

    const handleTagSearch = useCallback(
        async (searchText) => {
            if (searchText && searchText.length >= 1) {
                try {
                    const response = await getTags({
                        orgId: sessionStorage.getItem('userOrg'),
                        filter: searchText,
                    }).unwrap();

                    if (response?.content) {
                        // Track existing tags in the system
                        const existingTagNames = response.content.map((tag) => tag.name || tag.tag);
                        setExistingTags((prevExisting) => new Set([...prevExisting, ...existingTagNames]));

                        const suggestions = response.content
                            .filter((tag) => !value.includes(tag.name || tag.tag))
                            .map((tag) => ({
                                value: tag.name || tag.tag,
                                label: tag.name || tag.tag,
                            }));
                        setSuggestedTags(suggestions);
                    }
                } catch (error) {
                    console.error('Error fetching tags:', error);
                    setSuggestedTags([]);
                }
            } else {
                setSuggestedTags([]);
            }
        },
        [getTags, value],
    );

    const handleAddTag = useCallback(() => {
        const trimmedTag = newTag.trim();
        if (trimmedTag && !value.includes(trimmedTag)) {
            const newTags = [...value, trimmedTag];

            // Determine which tags are new (not in existingTags set)
            const newTagsList = newTags.filter((tag) => !existingTags.has(tag));

            onChange?.(newTags, newTagsList);

            setNewTag('');
            setSuggestedTags([]);
        }
    }, [newTag, value, onChange, existingTags]);

    const handleRemoveTag = useCallback(
        (tagToRemove) => {
            const newTags = value.filter((tag) => tag !== tagToRemove);

            // Determine which tags are new (not in existingTags set)
            const newTagsList = newTags.filter((tag) => !existingTags.has(tag));

            onChange?.(newTags, newTagsList);
        },
        [value, onChange, existingTags],
    );

    const handleKeyPress = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
            }
        },
        [handleAddTag],
    );

    return (
        <div className={`tags-selector ${className}`} style={style}>
            <Space orientation="vertical" style={{ width: '100%' }}>
                <Row gutter={8}>
                    <Col flex="auto">
                        <AutoComplete
                            placeholder={placeholder}
                            value={newTag}
                            options={suggestedTags}
                            showSearch={{ onSearch: handleTagSearch, filterOption: false }}
                            onSelect={(selectedValue) => {
                                setNewTag(selectedValue);
                                setSuggestedTags([]);
                            }}
                            onKeyPress={handleKeyPress}
                            notFoundContent={
                                newTag.length >= 1 ? 'No matching tags found' : 'Start typing to search for tags'
                            }
                            style={{ width: '100%' }}
                            disabled={disabled}
                            onChange={setNewTag}
                        />
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddTag}
                            disabled={!newTag.trim() || disabled}
                        >
                            Add
                        </Button>
                    </Col>
                </Row>

                {value.length > 0 && (
                    <div className="tags-list">
                        {value.map((tag) => (
                            <RivermeadowTag
                                key={tag}
                                closable={!disabled}
                                onClose={() => handleRemoveTag(tag)}
                                closeIcon={<CloseOutlined />}
                            >
                                {tag}
                            </RivermeadowTag>
                        ))}
                    </div>
                )}
            </Space>
        </div>
    );
};

export default TagsSelector;
